import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { useWallet } from '../contexts/WalletContext'
import { getImageUrl } from '../utils/cdnUtils'
import { getPreferredUserIdentifier } from '../utils/userIdentity'
import {
  getActiveRewards,
  getPointsConfig,
  POINTS_CONFIG_UPDATED_EVENT,
  notifyUserSummaryUpdated,
  redeemReward,
  type PointsConfigData,
  type PointsReward,
} from '../services/pointsService'
import {
  hasRewardDescriptionDetail,
  runRewardRedeemFlow,
  type RewardRedeemItem,
} from '@huay/shared/points/rewardRedeemFlow'
import {
  createClassicRewardRedeemHandlers,
  showClassicRewardDetailModal,
} from './classicRewardRedeemHandlers'
import './pointsRewards.css'
import '@huay/shared/points/rewardsAnimations.css'

const getCurrentPoints = (userInfo: ReturnType<typeof useUserInfo>['userInfo']): number => {
  if (!userInfo) return 0
  const loyaltyPoints = Number(userInfo.loyaltyPoints?.currentPoints)
  if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
  const bonusPoints = Number((Array.isArray(userInfo.bonuses) ? userInfo.bonuses[0]?.points : 0) || 0)
  return Number.isFinite(bonusPoints) ? bonusPoints : 0
}

const formatNumber = (value: number) => Number(value || 0).toLocaleString('th-TH')

const PointsRewardsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { walletData, formatCurrency, refreshWallet } = useWallet()
  const { userInfo, refreshUserInfo } = useUserInfo()
  const [pointsConfig, setPointsConfig] = useState<PointsConfigData | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [submittingRewardId, setSubmittingRewardId] = useState<string | null>(null)
  const redeemHandlers = useMemo(() => createClassicRewardRedeemHandlers(), [])

  const tenantId = user?.tenantId || userInfo?.tenantId || ''
  const points = getCurrentPoints(userInfo)
  const pointsText = `${points.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`
  const balanceText = walletData ? `${formatCurrency(walletData.balance.main)} บาท` : '0.00 บาท'

  useEffect(() => {
    let mounted = true
    const loadConfig = async () => {
      if (!tenantId) {
        if (mounted) setIsLoadingConfig(false)
        return
      }
      try {
        const config = await getPointsConfig(tenantId)
        if (mounted) setPointsConfig(config)
      } finally {
        if (mounted) setIsLoadingConfig(false)
      }
    }
    void loadConfig()
    return () => {
      mounted = false
    }
  }, [tenantId])

  const refreshPointsConfig = useCallback(async () => {
    if (!tenantId) {
      setPointsConfig(null)
      setIsLoadingConfig(false)
      return
    }
    try {
      setIsLoadingConfig(true)
      const config = await getPointsConfig(tenantId)
      setPointsConfig(config)
    } finally {
      setIsLoadingConfig(false)
    }
  }, [tenantId])

  useEffect(() => {
    const handlePointsConfigUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string }>).detail
      if (detail?.tenantId && detail.tenantId !== tenantId) return
      void refreshPointsConfig()
    }
    window.addEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
    return () => window.removeEventListener(POINTS_CONFIG_UPDATED_EVENT, handlePointsConfigUpdated)
  }, [refreshPointsConfig, tenantId])

  const rewardsEnabled = pointsConfig?.system?.rewardsEnabled !== false
  const activeRewards = useMemo(() => getActiveRewards(pointsConfig?.rewards), [pointsConfig?.rewards])

  const handleRedeem = async (reward: RewardRedeemItem) => {
    if (!tenantId || !user) {
      await redeemHandlers.onError('ไม่พบข้อมูลผู้ใช้')
      return
    }
    const authIdentity = user as unknown as { _id?: string | null; id?: string | null; userId?: string | null }
    const identifier = getPreferredUserIdentifier({
      _id: userInfo?._id || authIdentity?._id,
      id: authIdentity?.id,
      userId: userInfo?.userId || authIdentity?.userId,
    })
    if (!identifier) {
      await redeemHandlers.onError('ไม่พบรหัสผู้ใช้')
      return
    }

    try {
      setSubmittingRewardId(reward.id)
      await runRewardRedeemFlow({
        reward,
        pointsBalance: points,
        handlers: redeemHandlers,
        redeem: async (shippingInfo) => {
          const response = await redeemReward({
            tenantId,
            userId: identifier,
            rewardId: reward.id,
            shippingInfo,
          })
          notifyUserSummaryUpdated()
          await Promise.all([refreshWallet(), refreshUserInfo()])
          return { message: response.message }
        },
      })
    } finally {
      setSubmittingRewardId(null)
    }
  }

  return (
    <div className="point-page-wrapper">
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-3 classic-rewards-panel huay-rewards-animate">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h4 className="mb-1">แลกรางวัล</h4>
                    <small className="text-muted">ใช้คะแนนสะสมเพื่อแลกเครดิตหรือของรางวัล</small>
                  </div>
                  <a
                    href="#"
                    className="txt-link-d"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate('/dashboard')
                    }}
                  >
                    <i className="fas fa-chevron-left me-1"></i>
                    หน้าหลัก
                  </a>
                </div>

                <div className="nav nav-pills nav-pills-ticket mx--1 mb-2" role="tablist">
                  <div className="nav-link col px-1 py-0">
                    <Link to="/points/moneytopoint" className="check-button w-100 d-block text-decoration-none bgc-4 c-dark">
                      <h4><i className="fas fa-wallet"></i></h4>
                      <p>เงินเป็นคะแนน</p>
                    </Link>
                  </div>
                  <div className="nav-link col px-1 py-0">
                    <Link to="/points/pointtomoney" className="check-button w-100 d-block text-decoration-none bgc-4 c-dark">
                      <h4><i className="fas fa-exchange-alt"></i></h4>
                      <p>คะแนนเป็นเงิน</p>
                    </Link>
                  </div>
                  <div className="nav-link col px-1 py-0 active">
                    <Link to="/points/rewards" className="check-button w-100 d-block text-decoration-none bgg-5 c-white">
                      <h4><i className="fas fa-gift"></i></h4>
                      <p>ของรางวัล</p>
                    </Link>
                  </div>
                </div>

                <div className="row mx--1 mb-2">
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center classic-rewards-points-card">
                      <p className="c-dark mb-1">คะแนนสะสมปัจจุบัน</p>
                      <h5 className="c-red t-medium mb-0 classic-rewards-points-value">{pointsText}</h5>
                    </div>
                  </div>
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                      <p className="c-dark mb-1">เครดิตคงเหลือ</p>
                      <h5 className="c-main t-medium mb-0">{balanceText}</h5>
                    </div>
                  </div>
                  <div className="col-12 col-md-4 px-1 mb-2">
                    <div className="bgc-4 bdr-5 p-2 h-100 text-center classic-rewards-status-card">
                      <p className="c-dark mb-1">สถานะระบบแลกรางวัล</p>
                      <h6 className={`mb-0 t-medium ${rewardsEnabled && activeRewards.length > 0 ? 'c-green' : 'c-red'}`}>
                        {isLoadingConfig
                          ? 'กำลังโหลด...'
                          : rewardsEnabled && activeRewards.length > 0
                            ? 'พร้อมใช้งาน'
                            : rewardsEnabled
                              ? 'ยังไม่มีรางวัลที่เปิดใช้งาน'
                              : 'ปิดระบบแลกรางวัล'}
                      </h6>
                    </div>
                  </div>
                </div>

                {rewardsEnabled && activeRewards.length > 0 ? (
                  <div className="row mx--1">
                    {activeRewards.map((reward: PointsReward, index: number) => {
                      const affordable = points >= Number(reward.pointsCost)
                      const description = reward.type === 'credit'
                        ? `รับเครดิต ${formatNumber(reward.creditAmount)} บาท`
                        : reward.description
                      const showDetail = hasRewardDescriptionDetail(description)

                      return (
                        <div key={reward.id} className="col-12 col-md-6 px-1 mb-2">
                          <div
                            className="bgc-4 bdr-5 p-2 h-100 d-flex flex-column classic-reward-card"
                            style={{ '--reward-enter-delay': `${0.16 + Math.min(index, 10) * 0.05}s` } as React.CSSProperties}
                          >
                            <div className="wrap align-items-center mb-2">
                              <div className="img-b bdr-5 bgc-7">
                                <img src={reward.imageUrl || getImageUrl('logo-m.png')} alt={reward.name} />
                              </div>
                              <div className="txt-b ms-3">
                                <h6 className="c-dark mb-1">{reward.name}</h6>
                                <h5 className="c-red t-medium mb-1">{formatNumber(reward.pointsCost)} คะแนน</h5>
                                {description ? (
                                  <div>
                                    <p className="c-dark fs-14 mb-0 classic-reward-card__desc">{description}</p>
                                    {showDetail ? (
                                      <button
                                        type="button"
                                        className="classic-reward-card__detail-badge"
                                        onClick={() =>
                                          void showClassicRewardDetailModal({
                                            name: reward.name,
                                            description,
                                            imageUrl: reward.imageUrl || getImageUrl('logo-m.png'),
                                            rewardType: reward.type,
                                            stock: reward.stock,
                                          })
                                        }
                                      >
                                        รายละเอียด
                                      </button>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              className={`check-button w-100 border-0 c-white mt-auto bgg-5${affordable ? '' : ' opacity-75'}`}
                              onClick={() => void handleRedeem(reward)}
                              disabled={submittingRewardId === reward.id}
                            >
                              <i className="fas fa-gift me-2"></i>
                              {submittingRewardId === reward.id
                                ? 'กำลังทำรายการ...'
                                : affordable
                                  ? 'แลกรางวัล'
                                  : 'คะแนนไม่พอ'}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="bgc-4 bdr-5 p-3 text-center classic-rewards-empty">
                    <h6 className="c-dark mb-1">ยังไม่พร้อมใช้งาน</h6>
                    <p className="c-dark mb-0">
                      {isLoadingConfig
                        ? 'กำลังโหลดรายการรางวัล...'
                        : rewardsEnabled
                          ? 'ยังไม่มีรางวัลที่เปิดให้แลกในขณะนี้'
                          : 'ระบบแลกรางวัลถูกปิดอยู่'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PointsRewardsPage
